import { makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { deleteDataV2, fetchDataV2 } from "../../../api/utils";
import { ErrorMsg } from "../../../components/AppMessages";
import { TORCH_RED, TORCH_RED_BRIGHT } from "../../../constants";
import UserSearchField from "../common/Fields/UserSearchField";
import DeleteUserViewDialog from "./subs/Dialogs/DeleteUserViewDialog";
import UserViewFormDialog from "./subs/Dialogs/UserViewsFormDialog";
import UserViewsTable from "./subs/UserViewsTable";

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
// Sized down from MUI's own "medium" Button default (0.875rem / 6px 16px) - matches the same
// reduction applied across every other Admin tile's New/Edit/Delete buttons (see
// adminActionButtonStyles.ts), this one just predates that shared file and still duplicates it
// locally.
const useStyles = makeStyles({
  button: {
    backgroundColor: TORCH_RED,
    color: "#FFF !important",
    borderRadius: "0px !important",
    border: "none !important",
    marginRight: "10px",
    fontSize: "0.75rem !important",
    padding: "4px 10px !important",
    "&:hover": {
      backgroundColor: `${TORCH_RED_BRIGHT} !important`,
      border: "none !important",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
  buttonOutlined: {
    backgroundColor: "transparent !important",
    color: `${TORCH_RED} !important`,
    borderRadius: "0px !important",
    border: `1px solid ${TORCH_RED} !important`,
    marginRight: "10px",
    fontSize: "0.75rem !important",
    padding: "4px 10px !important",
    "&:hover": {
      backgroundColor: "rgba(244, 2, 42, 0.15) !important",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
    ".theme-dark &": {
      color: "#FFF !important",
    },
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const UserViews = () => {
  const { views } = useSelector((state: RootStateOrAny) => state.admin);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userViewFormDialogOpen, setUserViewDialogFormOpen] = useState<boolean>(false);
  const [selectedUserView, setSelectedUserView] = useState<any>(undefined);
  const [userViews, setUserViews] = useState<Array<any>>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const filteredUserViews = userViews.filter((userView) => userView.name.toLowerCase().includes(searchValue.toLowerCase()));

  const classes = useStyles();

  useEffect(() => {
    fetchDataV2("/user_service/get-all-view-groups").then((res) => {
      // handleError (api/utils.ts) returns undefined for a cancelled request or when the user
      // has already been logged out - neither case is an error to report here.
      if (!res) return;
      if (!res.error) {
        setUserViews(res);
      } else {
        console.log(res.error);
      }
    });
  }, []);

  const handleToogleDeleteDialog = () => {
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleToggleUserViewFormDialog = () => {
    setUserViewDialogFormOpen(!userViewFormDialogOpen);
    if (userViewFormDialogOpen) {
      setSelectedUserView(undefined);
    }
  };

  const handleDeleteUserView = (id: number) => {
    deleteDataV2(`/user_service/delete-view-group?id=${id}`).then((res) => {
      if (!res.error) {
        setUserViews((prevState) => prevState.filter((user) => user.id !== id));
        setSelectedUserView(undefined);
        handleToogleDeleteDialog();
      } else {
        console.log(res.error);
      }
    });
  };

  return (
    <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
      <Box component="div" className="admin_tile">
        <Box className={classes.buttonsContainer} component="div" mb={3}>
          <Box component="div">
            <Button
              className={classes.button}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={() => {
                setSelectedUserView(undefined);
                handleToggleUserViewFormDialog();
              }}
              data-test-id="btn-add-new-user-view"
            >
              New
            </Button>
            <Button
              className={classes.buttonOutlined}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={handleToggleUserViewFormDialog}
              disabled={!selectedUserView}
              data-test-id="btn-edit-user-view"
            >
              Edit
            </Button>
            <Button
              className={classes.buttonOutlined}
              variant="outlined"
              disableTouchRipple
              disableFocusRipple
              onClick={handleToogleDeleteDialog}
              disabled={!selectedUserView}
              data-test-id="btn-delete-user-view"
            >
              Delete
            </Button>
          </Box>
          <Box component="div">
            <UserSearchField value={searchValue} setValue={setSearchValue} />
          </Box>
        </Box>

        <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
          <UserViewsTable
            selectedUserView={selectedUserView}
            setSelectedUserView={setSelectedUserView}
            setDeleteDialogOpen={setDeleteDialogOpen}
            userViews={filteredUserViews}
            views={views}
            searchValue={searchValue}
          />
        </Sentry.ErrorBoundary>

        {deleteDialogOpen && selectedUserView && (
          <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
            <DeleteUserViewDialog
              open={deleteDialogOpen}
              handleClose={handleToogleDeleteDialog}
              handleDelete={handleDeleteUserView}
              userView={selectedUserView}
            />
          </Sentry.ErrorBoundary>
        )}

        {userViewFormDialogOpen && (
          <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
            <UserViewFormDialog
              open={userViewFormDialogOpen}
              toggleOpen={handleToggleUserViewFormDialog}
              setUserViews={setUserViews}
              selectedUserView={selectedUserView}
              views={views}
            />
          </Sentry.ErrorBoundary>
        )}
      </Box>
    </Sentry.ErrorBoundary>
  );
};

export default UserViews;
